import { useCallback, useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { ChevronRight } from 'lucide-react';
import type { HomeGame } from '@/types/game';
import { GameCard } from './game-card';
import { Link } from 'wouter';
import { CarouselNavButton } from './carousel-nav-button';
import * as changeCase from 'change-case';

type GameCarouselProps = {
  title?: string;
  categoryId?: number;
  games: HomeGame[];
  disableNavigation?: boolean;
};

export function GameCarousel({ title, categoryId, games, disableNavigation }: GameCarouselProps) {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: true,
    },
    [WheelGesturesPlugin()],
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();

    emblaApi.on('select', onSelect);
    emblaApi.on('scroll', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('scroll', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Reset carousel position when games array changes
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.scrollTo(0);
  }, [games, emblaApi]);

  return (
    <div className="relative">
      {title && (
        <div className="mb-[1rem] md:mb-[2rem]">
          {disableNavigation ? (
            <div className="inline-flex text-3xl font-bold text-customPurple-100 text-3xl sm:text-4xl font-extrabold">
              <span>{changeCase.sentenceCase(title)}</span>
            </div>
          ) : (
            <Link href={`/games?categoryId=${categoryId}`}>
              <div className="inline-flex text-3xl font-bold text-customPurple-100 hover:text-customPurple-200 transition-colors text-3xl sm:text-4xl font-extrabold">
                <span>{changeCase.sentenceCase(title)}</span>
                <ChevronRight className="self-center stroke-customPurple-400 w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12" />
              </div>
            </Link>
          )}
        </div>
      )}
      <div className="overflow-visible relative group" ref={viewportRef}>
        <div className="flex">
          {games.map((game, index) => (
            <div
              key={index}
              className="flex-none overflow-visible sm:h-[320px] w-full sm:w-[560px] lg:w-[32%] lg:h-full  mr-[0.4rem] lg:mr-[1rem] last:mr-0"
            >
              <GameCard key={game.id} game={game} />
            </div>
          ))}
        </div>
        {canScrollPrev && <CarouselNavButton direction="prev" onClick={scrollPrev} />}
        {canScrollNext && <CarouselNavButton direction="next" onClick={scrollNext} />}
      </div>
    </div>
  );
}
